/**
 * AutoTradeRules - Automated Trading Rule Builder
 *
 * Create, manage, and monitor automated trading rules that execute
 * based on AI signal conditions. Includes kill switch for emergency
 * shutdown of all automated activity.
 *
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Bot,
  Plus,
  Trash2,
  Power,
  PowerOff,
  AlertOctagon,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Shield,
  Zap,
  Activity,
  Clock,
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Settings,
  BarChart3,
  DollarSign,
  Percent,
  Hash,
  FileText,
  Loader2,
} from 'lucide-react';
import { brokerAPI, type AutoTradeRule } from '../../api/broker';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AutoTradeLogEntry {
  log_id: string;
  rule_id: string;
  rule_name: string;
  symbol: string;
  side: string;
  qty: number;
  price: number;
  status: string;
  signal_type: string;
  confidence: number;
  executed_at: string;
  error_message?: string | null;
}

interface RuleFormState {
  name: string;
  description: string;
  symbol: string;
  signal_type: string;
  min_confidence: number;
  min_growth_score: number;
  action_side: string;
  order_type: string;
  qty_type: string;
  qty_value: number;
  auto_stop_loss_pct: number;
  auto_take_profit_pct: number;
  max_daily_trades: number;
  max_daily_loss_usd: number;
  max_position_pct: number;
  cooldown_minutes: number;
}

const DEFAULT_FORM: RuleFormState = {
  name: '',
  description: '',
  symbol: '',
  signal_type: 'STRONG_BUY',
  min_confidence: 70,
  min_growth_score: 60,
  action_side: 'buy',
  order_type: 'market',
  qty_type: 'fixed',
  qty_value: 1,
  auto_stop_loss_pct: 5,
  auto_take_profit_pct: 10,
  max_daily_trades: 5,
  max_daily_loss_usd: 500,
  max_position_pct: 10,
  cooldown_minutes: 30,
};

const SIGNAL_TYPES = ['STRONG_BUY', 'BUY', 'SELL', 'STRONG_SELL'] as const;

// ─── Component ──────────────────────────────────────────────────────────────

export default function AutoTradeRules() {
  const [rules, setRules] = useState<AutoTradeRule[]>([]);
  const [log, setLog] = useState<AutoTradeLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState<RuleFormState>({ ...DEFAULT_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [killSwitchActive, setKillSwitchActive] = useState(false);
  const [confirmKill, setConfirmKill] = useState(false);
  const [expandedRule, setExpandedRule] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'rules' | 'log'>('rules');
  const [togglingRule, setTogglingRule] = useState<string | null>(null);
  const [deletingRule, setDeletingRule] = useState<string | null>(null);

  // ─── Data Fetching ────────────────────────────────────────────────────

  const fetchRules = useCallback(async () => {
    try {
      const data = await brokerAPI.getAutoTradeRules();
      setRules(data.rules || []);
    } catch (err: any) {
      console.error('Failed to fetch rules:', err);
      setError(err.message || 'Failed to load auto-trade rules');
    }
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      const data = await brokerAPI.getAutoTradeLog();
      setLog((data as any).log || (data as any).entries || []);
    } catch (err: any) {
      console.error('Failed to fetch log:', err);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchRules(), fetchLog()]);
    setLoading(false);
  }, [fetchRules, fetchLog]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  const handleCreateRule = async () => {
    if (!form.name.trim()) {
      setError('Rule name is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await brokerAPI.createAutoTradeRule({
        name: form.name.trim(),
        description: form.description.trim() || null,
        enabled: true,
        symbol: form.symbol.trim().toUpperCase() || null,
        signal_type: form.signal_type,
        min_confidence: form.min_confidence / 100,
        min_growth_score: form.min_growth_score,
        action_side: form.action_side,
        order_type: form.order_type,
        qty_type: form.qty_type,
        qty_value: form.qty_value,
        auto_stop_loss_pct: form.auto_stop_loss_pct,
        auto_take_profit_pct: form.auto_take_profit_pct,
        max_daily_trades: form.max_daily_trades,
        max_daily_loss_usd: form.max_daily_loss_usd,
        max_position_pct: form.max_position_pct,
        cooldown_minutes: form.cooldown_minutes,
      } as Partial<AutoTradeRule>);

      setSuccess('Rule created successfully');
      setForm({ ...DEFAULT_FORM });
      setShowCreateForm(false);
      await fetchRules();
    } catch (err: any) {
      setError(err.message || 'Failed to create rule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleRule = async (rule: AutoTradeRule) => {
    setTogglingRule(rule.rule_id);
    try {
      await brokerAPI.updateAutoTradeRule(rule.rule_id, { enabled: !rule.enabled });
      setRules((prev) =>
        prev.map((r) => (r.rule_id === rule.rule_id ? { ...r, enabled: !r.enabled } : r))
      );
      setSuccess(`Rule "${rule.name}" ${rule.enabled ? 'disabled' : 'enabled'}`);
    } catch (err: any) {
      setError(err.message || 'Failed to toggle rule');
    } finally {
      setTogglingRule(null);
    }
  };

  const handleDeleteRule = async (ruleId: string, ruleName: string) => {
    setDeletingRule(ruleId);
    try {
      await brokerAPI.deleteAutoTradeRule(ruleId);
      setRules((prev) => prev.filter((r) => r.rule_id !== ruleId));
      setSuccess(`Rule "${ruleName}" deleted`);
    } catch (err: any) {
      setError(err.message || 'Failed to delete rule');
    } finally {
      setDeletingRule(null);
    }
  };

  const handleKillSwitch = async () => {
    if (!confirmKill) {
      setConfirmKill(true);
      setTimeout(() => setConfirmKill(false), 5000);
      return;
    }

    setKillSwitchActive(true);
    setConfirmKill(false);
    try {
      await brokerAPI.killSwitch();
      setSuccess('KILL SWITCH ACTIVATED: All rules disabled, all orders cancelled');
      await fetchRules();
    } catch (err: any) {
      setError(err.message || 'Kill switch failed');
    } finally {
      setKillSwitchActive(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────

  const getSignalBadge = (signal: string) => {
    switch (signal) {
      case 'STRONG_BUY':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'BUY':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'SELL':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'STRONG_SELL':
        return 'bg-red-600/20 text-red-300 border-red-600/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'filled':
      case 'success':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'failed':
      case 'error':
        return 'bg-red-500/20 text-red-400';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const formatConditions = (rule: AutoTradeRule): string => {
    const parts: string[] = [];
    if (rule.symbol) parts.push(rule.symbol);
    if (rule.signal_type) parts.push(rule.signal_type.replace('_', ' '));
    if (rule.min_confidence != null)
      parts.push(`>${Math.round(rule.min_confidence * 100)}% conf`);
    if (rule.min_growth_score != null) parts.push(`>${rule.min_growth_score} growth`);
    return parts.join(' | ') || 'Any signal';
  };

  const formatAction = (rule: AutoTradeRule): string => {
    const sideLabel = rule.action_side === 'buy' ? 'BUY' : 'SELL';
    const qtyLabel =
      rule.qty_type === 'fixed'
        ? `${rule.qty_value} shares`
        : rule.qty_type === 'pct_equity'
          ? `${rule.qty_value}% equity`
          : `risk-based ${rule.qty_value}`;
    return `${sideLabel} ${qtyLabel} (${rule.order_type})`;
  };

  // ─── Render ───────────────────────────────────────────────────────────

  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-6">
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bot className="w-8 h-8 text-violet-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Auto-Trade Rules</h1>
            <p className="text-gray-400 text-sm">
              {rules.length} rule{rules.length !== 1 ? 's' : ''} configured
              {enabledCount > 0 && (
                <span className="text-emerald-400 ml-2">
                  ({enabledCount} active)
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchAll}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-[#12121a] border border-[#1e1e2d] rounded-lg hover:border-violet-500/50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <button
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              if (!showCreateForm) setForm({ ...DEFAULT_FORM });
            }}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Rule
          </button>

          {/* Kill Switch */}
          <button
            onClick={handleKillSwitch}
            disabled={killSwitchActive}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold transition-all ${
              confirmKill
                ? 'bg-red-600 hover:bg-red-700 text-white ring-2 ring-red-400 ring-offset-2 ring-offset-[#0a0a0f] animate-pulse'
                : 'bg-red-900/40 border border-red-500/40 text-red-400 hover:bg-red-900/70 hover:border-red-500'
            }`}
          >
            {killSwitchActive ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <AlertOctagon className="w-4 h-4" />
            )}
            {confirmKill ? 'CONFIRM KILL SWITCH' : 'Kill Switch'}
          </button>
        </div>
      </div>

      {/* ── Status Messages ──────────────────────────────────────── */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-emerald-900/20 border border-emerald-500/50 rounded-lg p-4 mb-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <span className="text-emerald-400 text-sm">{success}</span>
        </div>
      )}

      {/* ── Section Tabs ─────────────────────────────────────────── */}
      <div className="flex gap-2 border-b border-[#1e1e2d] mb-6">
        {[
          { id: 'rules' as const, label: 'Rules', icon: Settings, count: rules.length },
          { id: 'log' as const, label: 'Execution Log', icon: FileText, count: log.length },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeSection === tab.id
                ? 'border-violet-500 text-violet-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            <span className="ml-1 text-xs bg-[#1e1e2d] px-2 py-0.5 rounded-full">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* ── Create Rule Form ─────────────────────────────────────── */}
      {showCreateForm && (
        <div className="bg-[#12121a] border border-violet-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-6">
            <Zap className="w-5 h-5 text-violet-400" />
            <h2 className="text-lg font-semibold text-white">Create New Rule</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ─ Basic Info ─ */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-4 h-4" /> Basic Info
              </h3>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Rule Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Strong Buy Scalper"
                  className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white placeholder:text-gray-600 focus:outline-none focus:border-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description of what this rule does..."
                  rows={2}
                  className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white placeholder:text-gray-600 focus:outline-none focus:border-violet-500 resize-none"
                />
              </div>
            </div>

            {/* ─ Conditions ─ */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <Target className="w-4 h-4" /> Conditions
              </h3>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Symbol (optional)</label>
                <input
                  type="text"
                  value={form.symbol}
                  onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                  placeholder="e.g. AAPL (leave blank for any)"
                  className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white placeholder:text-gray-600 focus:outline-none focus:border-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Signal Type</label>
                <select
                  value={form.signal_type}
                  onChange={(e) => setForm({ ...form, signal_type: e.target.value })}
                  className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                >
                  {SIGNAL_TYPES.map((s) => (
                    <option key={s} value={s}>
                      {s.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Min Confidence: <span className="text-violet-400 font-medium">{form.min_confidence}%</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={form.min_confidence}
                  onChange={(e) => setForm({ ...form, min_confidence: Number(e.target.value) })}
                  className="w-full accent-violet-500"
                />
                <div className="flex justify-between text-xs text-gray-600">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Min Growth Score: <span className="text-violet-400 font-medium">{form.min_growth_score}</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={form.min_growth_score}
                  onChange={(e) => setForm({ ...form, min_growth_score: Number(e.target.value) })}
                  className="w-full accent-violet-500"
                />
                <div className="flex justify-between text-xs text-gray-600">
                  <span>0</span>
                  <span>50</span>
                  <span>100</span>
                </div>
              </div>
            </div>

            {/* ─ Action ─ */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <Activity className="w-4 h-4" /> Action
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Side</label>
                  <select
                    value={form.action_side}
                    onChange={(e) => setForm({ ...form, action_side: e.target.value })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  >
                    <option value="buy">Buy</option>
                    <option value="sell">Sell</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Order Type</label>
                  <select
                    value={form.order_type}
                    onChange={(e) => setForm({ ...form, order_type: e.target.value })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  >
                    <option value="market">Market</option>
                    <option value="limit">Limit</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Qty Type</label>
                  <select
                    value={form.qty_type}
                    onChange={(e) => setForm({ ...form, qty_type: e.target.value })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  >
                    <option value="fixed">Fixed Qty</option>
                    <option value="pct_equity">% of Equity</option>
                    <option value="risk_based">Risk-Based</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    {form.qty_type === 'fixed'
                      ? 'Shares'
                      : form.qty_type === 'pct_equity'
                        ? '% of Equity'
                        : 'Risk Multiple'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={form.qty_type === 'fixed' ? 1 : 0.5}
                    value={form.qty_value}
                    onChange={(e) => setForm({ ...form, qty_value: Number(e.target.value) })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>
            </div>

            {/* ─ Risk Management ─ */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <Shield className="w-4 h-4" /> Risk Management
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Auto Stop-Loss %</label>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    step={0.5}
                    value={form.auto_stop_loss_pct}
                    onChange={(e) => setForm({ ...form, auto_stop_loss_pct: Number(e.target.value) })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Auto Take-Profit %</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={form.auto_take_profit_pct}
                    onChange={(e) => setForm({ ...form, auto_take_profit_pct: Number(e.target.value) })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>
            </div>

            {/* ─ Limits ─ */}
            <div className="space-y-4 lg:col-span-2">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Limits
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Daily Trades</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={form.max_daily_trades}
                    onChange={(e) => setForm({ ...form, max_daily_trades: Number(e.target.value) })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Daily Loss ($)</label>
                  <input
                    type="number"
                    min={0}
                    step={50}
                    value={form.max_daily_loss_usd}
                    onChange={(e) => setForm({ ...form, max_daily_loss_usd: Number(e.target.value) })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Position (% equity)</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={form.max_position_pct}
                    onChange={(e) => setForm({ ...form, max_position_pct: Number(e.target.value) })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Cooldown (min)</label>
                  <input
                    type="number"
                    min={0}
                    max={1440}
                    value={form.cooldown_minutes}
                    onChange={(e) => setForm({ ...form, cooldown_minutes: Number(e.target.value) })}
                    className="w-full bg-[#0a0a0f] border border-[#1e1e2d] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-[#1e1e2d]">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateRule}
              disabled={submitting || !form.name.trim()}
              className="flex items-center gap-2 px-6 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Create Rule
            </button>
          </div>
        </div>
      )}

      {/* ── Rules Section ────────────────────────────────────────── */}
      {activeSection === 'rules' && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-violet-400 animate-spin" />
            </div>
          ) : rules.length === 0 ? (
            <div className="bg-[#12121a] border border-[#1e1e2d] rounded-xl p-12 text-center">
              <Bot className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-400 mb-2">No Auto-Trade Rules</h3>
              <p className="text-gray-500 text-sm mb-6">
                Create your first rule to start automated trading based on AI signals.
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Your First Rule
              </button>
            </div>
          ) : (
            rules.map((rule) => {
              const isExpanded = expandedRule === rule.rule_id;
              return (
                <div
                  key={rule.rule_id}
                  className={`bg-[#12121a] border rounded-xl transition-colors ${
                    rule.enabled
                      ? 'border-violet-500/30 shadow-lg shadow-violet-500/5'
                      : 'border-[#1e1e2d] opacity-70'
                  }`}
                >
                  {/* Rule Header */}
                  <div className="p-4 flex items-center gap-4">
                    {/* Enable/Disable Toggle */}
                    <button
                      onClick={() => handleToggleRule(rule)}
                      disabled={togglingRule === rule.rule_id}
                      className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
                        rule.enabled ? 'bg-violet-600' : 'bg-gray-700'
                      }`}
                      title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                    >
                      {togglingRule === rule.rule_id ? (
                        <Loader2 className="w-4 h-4 text-white animate-spin absolute top-1 left-4" />
                      ) : (
                        <div
                          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            rule.enabled ? 'translate-x-6' : 'translate-x-0.5'
                          }`}
                        />
                      )}
                    </button>

                    {/* Rule Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium truncate">{rule.name}</h3>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs rounded border ${getSignalBadge(
                            rule.signal_type || ''
                          )}`}
                        >
                          {rule.signal_type?.replace('_', ' ') || 'Any'}
                        </span>
                        {rule.symbol && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                            {rule.symbol}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          {formatConditions(rule)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Activity className="w-3 h-3" />
                          {formatAction(rule)}
                        </span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-gray-500 text-xs">Today</div>
                        <div className="text-white font-medium">{rule.trades_today}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500 text-xs">Total</div>
                        <div className="text-white font-medium">{rule.total_trades}</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedRule(isExpanded ? null : rule.rule_id)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-[#1e1e2d] rounded-lg transition-colors"
                        title="Details"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.rule_id, rule.name)}
                        disabled={deletingRule === rule.rule_id}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete rule"
                      >
                        {deletingRule === rule.rule_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-[#1e1e2d] p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {rule.description && (
                          <div className="col-span-full text-sm text-gray-400 mb-2">
                            {rule.description}
                          </div>
                        )}
                        <DetailItem
                          icon={<Shield className="w-3.5 h-3.5" />}
                          label="Stop-Loss"
                          value={rule.auto_stop_loss_pct != null ? `${rule.auto_stop_loss_pct}%` : 'None'}
                        />
                        <DetailItem
                          icon={<TrendingUp className="w-3.5 h-3.5" />}
                          label="Take-Profit"
                          value={rule.auto_take_profit_pct != null ? `${rule.auto_take_profit_pct}%` : 'None'}
                        />
                        <DetailItem
                          icon={<Hash className="w-3.5 h-3.5" />}
                          label="Max Daily Trades"
                          value={rule.max_daily_trades != null ? String(rule.max_daily_trades) : 'No limit'}
                        />
                        <DetailItem
                          icon={<DollarSign className="w-3.5 h-3.5" />}
                          label="Max Daily Loss"
                          value={rule.max_daily_loss_usd != null ? `$${rule.max_daily_loss_usd}` : 'No limit'}
                        />
                        <DetailItem
                          icon={<Percent className="w-3.5 h-3.5" />}
                          label="Max Position"
                          value={rule.max_position_pct != null ? `${rule.max_position_pct}% equity` : 'No limit'}
                        />
                        <DetailItem
                          icon={<Clock className="w-3.5 h-3.5" />}
                          label="Cooldown"
                          value={rule.cooldown_minutes != null ? `${rule.cooldown_minutes} min` : 'None'}
                        />
                        <DetailItem
                          icon={<Clock className="w-3.5 h-3.5" />}
                          label="Last Triggered"
                          value={
                            rule.last_triggered
                              ? new Date(rule.last_triggered).toLocaleString()
                              : 'Never'
                          }
                        />
                        <DetailItem
                          icon={<Clock className="w-3.5 h-3.5" />}
                          label="Created"
                          value={
                            rule.created_at
                              ? new Date(rule.created_at).toLocaleDateString()
                              : 'Unknown'
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Execution Log Section ────────────────────────────────── */}
      {activeSection === 'log' && (
        <div className="bg-[#12121a] border border-[#1e1e2d] rounded-xl overflow-hidden">
          {log.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-400 mb-2">No Execution History</h3>
              <p className="text-gray-500 text-sm">
                Trades executed by auto-trade rules will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1e1e2d]">
                    <th className="text-left text-gray-400 font-medium px-4 py-3">Time</th>
                    <th className="text-left text-gray-400 font-medium px-4 py-3">Rule</th>
                    <th className="text-left text-gray-400 font-medium px-4 py-3">Symbol</th>
                    <th className="text-left text-gray-400 font-medium px-4 py-3">Side</th>
                    <th className="text-right text-gray-400 font-medium px-4 py-3">Qty</th>
                    <th className="text-right text-gray-400 font-medium px-4 py-3">Price</th>
                    <th className="text-left text-gray-400 font-medium px-4 py-3">Signal</th>
                    <th className="text-right text-gray-400 font-medium px-4 py-3">Confidence</th>
                    <th className="text-left text-gray-400 font-medium px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((entry) => (
                    <tr
                      key={entry.log_id}
                      className="border-b border-[#1e1e2d]/50 hover:bg-[#1a1a25] transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                        {entry.executed_at
                          ? new Date(entry.executed_at).toLocaleString()
                          : '--'}
                      </td>
                      <td className="px-4 py-3 text-white truncate max-w-[160px]">
                        {entry.rule_name || entry.rule_id?.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3 text-white font-medium">{entry.symbol}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 ${
                            entry.side === 'buy' ? 'text-emerald-400' : 'text-red-400'
                          }`}
                        >
                          {entry.side === 'buy' ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : (
                            <TrendingDown className="w-3 h-3" />
                          )}
                          {entry.side?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white text-right">{entry.qty}</td>
                      <td className="px-4 py-3 text-white text-right">
                        ${entry.price?.toFixed(2) || '--'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs rounded border ${getSignalBadge(
                            entry.signal_type
                          )}`}
                        >
                          {entry.signal_type?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300">
                        {entry.confidence != null
                          ? `${(entry.confidence * 100).toFixed(0)}%`
                          : '--'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${getStatusBadge(
                            entry.status
                          )}`}
                        >
                          {entry.status === 'filled' || entry.status === 'success' ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : entry.status === 'failed' || entry.status === 'error' ? (
                            <XCircle className="w-3 h-3" />
                          ) : (
                            <Clock className="w-3 h-3" />
                          )}
                          {entry.status}
                        </span>
                        {entry.error_message && (
                          <div className="text-xs text-red-400 mt-1">{entry.error_message}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-gray-500">{icon}</span>
      <span className="text-gray-500">{label}:</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
